// src/prototype/enterprise/portfolio/portfolioDisplay.ts
// ============================================================
// DISPLAY-ONLY helpers for the Portfolio surface. NO net-new data here — owner + category
// now come from the spine (getPortfolioAsOf → PortfolioInitiativeVM). This module only maps
// spine values to labels/badge styles and derives the current display status from the spine's
// decision log. Styling mirrors Enterprise Prototype.dc.html · Surface A.
// ============================================================

import type { DecisionLogEntryVM, UseCaseStatusValue } from '../../data/adapters/enterpriseSpine';

// ---- status (DERIVED from the spine's decision log) -------------------------

export type DisplayStatus = 'Active' | 'Approved' | 'Funded' | 'Deferred' | 'Hold' | 'Killed';

const STATUS_LABEL: Record<UseCaseStatusValue, DisplayStatus> = {
  CANDIDATE: 'Active',
  SHORTLISTED: 'Active',
  APPROVED: 'Approved',
  FUNDED: 'Funded',
  DEFERRED: 'Deferred',
  ON_HOLD: 'Hold',
  KILLED: 'Killed',
};

/** Tailwind classes for each status badge (mirrors the design's ST styles). */
export const STATUS_BADGE: Record<DisplayStatus, string> = {
  Active: 'bg-[#dcfce7] text-[#15803d]',
  Approved: 'bg-[#18365E] text-white',
  Funded: 'bg-[#18365E] text-white',
  Deferred: 'bg-[#e2e8f0] text-[#475569]',
  Hold: 'bg-[#e2e8f0] text-[#475569]',
  Killed: 'bg-[#fee2e2] text-[#b91c1c]',
};

/**
 * Current display status per use_case_id, from the spine's decision log (latest decision's
 * status_after; 'Active' when an initiative has no governance decision yet).
 */
export function statusByUseCase(decisionLog: DecisionLogEntryVM[]): Map<number, DisplayStatus> {
  const map = new Map<number, DisplayStatus>();
  // getDecisionLog returns newest-first, so the first seen per use case is the latest.
  for (const d of decisionLog) {
    if (!map.has(d.use_case_id)) map.set(d.use_case_id, STATUS_LABEL[d.status_after] ?? 'Active');
  }
  return map;
}

/** Confidence pill classes by percentage (design thresholds 70 / 50). */
export const confidenceBadge = (pct: number): string =>
  pct >= 70 ? 'bg-[#dcfce7] text-[#15803d]' : pct >= 50 ? 'bg-[#fef3c7] text-[#b45309]' : 'bg-[#fee2e2] text-[#b91c1c]';

/** The spine's filler titles carry a "· Initiative N" suffix; drop it for the grid name. */
export const initiativeName = (title: string): string => title.replace(/\s*·\s*Initiative\s+\d+$/, '');
